import React, { useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";

export default function Contact() {
    const [sent, setSent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setSent(true);
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <div className="max-w-[1200px] mx-auto px-4 md:px-10 pt-14 md:pt-20 pb-20 grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14">
                <div>
                    <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-3">
                        Contact
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
                        Let's <span className="silver-text italic">talk.</span>
                    </h1>
                    <p className="text-silver-400 mt-5 text-sm md:text-base max-w-md leading-relaxed">
                        Creators, buyers, investors, journalists — we read everything. Expect
                        a response within one business day (Mumbai time).
                    </p>

                    <div className="mt-10 space-y-5 text-sm text-silver-300">
                        <div className="flex items-start gap-3">
                            <Mail className="w-4 h-4 mt-0.5 text-platinum" />
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-silver-500">Email</div>
                                <div>hello@silverrights.io</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Phone className="w-4 h-4 mt-0.5 text-platinum" />
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-silver-500">Phone</div>
                                <div>+91 22 6XXX XXXX</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 mt-0.5 text-platinum" />
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-silver-500">Office</div>
                                <div>Andheri West, Mumbai, India</div>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={submit} className="glass p-6 md:p-8 rounded-sm" data-testid="contact-form">
                    {sent ? (
                        <div className="text-center py-16" data-testid="contact-sent">
                            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-5" />
                            <h3 className="font-display text-2xl mb-2">Message received.</h3>
                            <p className="text-silver-400 text-sm">We'll be in touch shortly.</p>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {["name", "email"].map((k) => (
                                <div key={k}>
                                    <label className="block text-[10px] uppercase tracking-widest text-silver-500 mb-1.5">
                                        {k === "name" ? "Full name" : "Email"}
                                    </label>
                                    <input
                                        type={k === "email" ? "email" : "text"}
                                        required
                                        value={form[k]}
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="w-full bg-transparent border-b border-white/15 focus:border-platinum focus:outline-none py-2 text-white"
                                        data-testid={`contact-${k}`}
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-silver-500 mb-1.5">
                                    Topic
                                </label>
                                <select
                                    value={form.topic}
                                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                                    className="w-full bg-ink-200 border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-platinum"
                                    data-testid="contact-topic"
                                >
                                    {["General", "Press", "Creator onboarding", "Buyer verification", "Investor / Partnerships"].map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-silver-500 mb-1.5">
                                    Message
                                </label>
                                <textarea
                                    rows={5}
                                    required
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="w-full bg-transparent border border-white/15 focus:border-platinum focus:outline-none py-2 px-3 text-white rounded-sm"
                                    data-testid="contact-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="bg-platinum hover:bg-platinum-200 disabled:opacity-50 text-ink-300 font-medium px-6 py-3 rounded-sm uppercase tracking-widest text-xs"
                                data-testid="contact-submit"
                            >
                                {submitting ? "Sending…" : "Send message"}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
